import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, Phone, Briefcase, UserPlus, IndianRupee, BadgeCheck, AlertCircle, Clock, Zap, ChevronDown, FileText } from 'lucide-react';
import BulkImportDialog from '../components/BulkImportDialog';
import MarkdownEditor from '../components/MarkdownEditor';
import {
  initialForm, initialManualForm, EMP_TYPES, PERIODS, grades, inr,
} from './employees/_shared';
import EmployeesFilterBar from './employees/EmployeesFilterBar';
import EmployeesGrid from './employees/EmployeesGrid';

// Form constants, EMP_TYPES, PERIODS, grades, inr, StatusPill moved to ./employees/_shared.

export default function Employees() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === 'admin';
  const canManage = ['admin', 'hr'].includes(user?.role);
  const [list, setList] = useState([]);
  const [offboardings, setOffboardings] = useState([]);  // pending/active resignation docs (HR/admin only)
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');   // all|active|onboarding|resigning|terminated|rejected
  const [jobFilter, setJobFilter] = useState('all');         // all|full_time|contract|internship|freelancer|volunteer
  const [deptFilter, setDeptFilter] = useState('all');       // all|<dept name>
  const [open, setOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [manualForm, setManualForm] = useState(initialManualForm());
  const [editing, setEditing] = useState(null);
  const [taxPreview, setTaxPreview] = useState(null);
  const [rms, setRms] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [deptManagers, setDeptManagers] = useState({}); // department-scoped RMs for the new-employee form
  const [deptManagersManual, setDeptManagersManual] = useState({});
  const [rmMode, setRmMode] = useState('dropdown'); // 'dropdown' or 'email'
  const [rmEmail, setRmEmail] = useState('');
  const [rmModeManual, setRmModeManual] = useState('dropdown');
  const [rmEmailManual, setRmEmailManual] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Offer-letter R&R + T&C defaults (loaded once on first dialog open)
  const [offerTemplates, setOfferTemplates] = useState(null);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);

  const load = async () => {
    try {
      const [{ data }, rmRes, deptRes, brRes] = await Promise.all([
        api.get('/employees'),
        canManage ? api.get('/employees/reporting-managers') : Promise.resolve({ data: [] }),
        canManage ? api.get('/departments') : Promise.resolve({ data: [] }),
        canManage ? api.get('/branches') : Promise.resolve({ data: [] }),
      ]);
      setList(data); setRms(rmRes.data); setDepartments(deptRes.data); setBranches(brRes.data);
      // Load offboardings (for resigning status derivation) — HR/admin only
      if (canManage) {
        try {
          const [pend, act] = await Promise.all([
            api.get('/offboardings?status=pending').catch(() => ({ data: [] })),
            api.get('/offboardings?status=active').catch(() => ({ data: [] })),
          ]);
          setOffboardings([...(pend.data || []), ...(act.data || [])]);
        } catch (err) { console.debug('[load offboardings]', err); }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  // When department changes, fetch eligible reporting managers in that dept
  const loadDeptManagers = async (dept, target) => {
    if (!dept) {
      if (target === 'manual') setDeptManagersManual({}); else setDeptManagers({});
      return;
    }
    try {
      const { data } = await api.get(`/org/managers-for-department?department=${encodeURIComponent(dept)}`);
      if (target === 'manual') setDeptManagersManual({ [dept]: data });
      else setDeptManagers({ [dept]: data });
    } catch (err) { console.debug('[loadDeptManagers]', err); }
  };
  useEffect(() => { loadDeptManagers(form.department, 'new'); }, [form.department]); // eslint-disable-line
  useEffect(() => { loadDeptManagers(manualForm.department, 'manual'); }, [manualForm.department]); // eslint-disable-line

  const resolveRmEmail = async (email) => {
    try {
      const { data } = await api.post('/org/lookup-by-email', { email });
      return data.id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    }
  };

  // Handle prefill from recruitment (?prefill={...})
  useEffect(() => {
    const p = searchParams.get('prefill');
    if (p) {
      try {
        const data = JSON.parse(decodeURIComponent(p));
        // eslint-disable-next-line
        setForm({ ...initialForm, name: data.name || '', personal_email: data.email || '',
                  phone: data.phone || '', title: data.title || '', grade: data.grade || '',
                  department: data.department || '',
                  annual_ctc: data.annual_ctc ? String(Math.round(data.annual_ctc)) : '' });
        setOpen(true);
        toast.info('Prefilled from candidate application — review and send offer');
        setSearchParams({});
      } catch (err) { console.debug('[prefill parse]', err); }
    }
  }, [searchParams, setSearchParams]);

  // Live tax preview
  // Compute effective annual CTC for tax preview based on employment_type + period + pay_amount
  const effectiveCtc = (() => {
    if (form.employment_type === 'volunteer') return 0;
    const amt = +form.pay_amount || +form.annual_ctc || 0;
    if (!amt) return 0;
    if (form.salary_period === 'monthly') return amt * 12;
    if (form.salary_period === 'hourly') return amt * 40 * 52;
    return amt;
  })();
  useEffect(() => {
    let t;
    if (canManage && effectiveCtc > 0) {
      t = setTimeout(async () => {
        try {
          const { data } = await api.get(`/payroll/preview-tax?annual_ctc=${effectiveCtc}`);
          // eslint-disable-next-line
          setTaxPreview(data);
        } catch (err) { console.debug('[tax preview]', err); }
      }, 400);
      // eslint-disable-next-line
    } else setTaxPreview(null);
    return () => t && clearTimeout(t);
  }, [effectiveCtc, canManage]);

  const onCreate = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (body.pay_amount) body.pay_amount = +body.pay_amount; else delete body.pay_amount;
      if (body.annual_ctc) body.annual_ctc = +body.annual_ctc;
      else delete body.annual_ctc;
      if (body.employment_type === 'volunteer') { delete body.pay_amount; delete body.annual_ctc; }
      if (!body.title) delete body.title;
      if (!body.grade) delete body.grade;
      // Coerce offer-terms numerics
      ['casual_leaves', 'sick_leaves', 'weekly_hours', 'probation_months'].forEach(k => {
        if (body[k] === '' || body[k] == null) delete body[k];
        else body[k] = parseInt(body[k], 10) || 0;
      });
      // R&R + T&C — only send if user actually entered something (empty -> server uses defaults)
      ['roles_responsibilities', 'terms_conditions'].forEach(k => {
        if (!body[k] || !String(body[k]).trim()) delete body[k];
      });
      // Resolve email-based RM
      if (rmMode === 'email' && rmEmail.trim()) {
        const rmId = await resolveRmEmail(rmEmail.trim());
        if (!rmId) { setSaving(false); return; }
        body.reporting_manager_id = rmId;
      }
      if (!body.reporting_manager_id) delete body.reporting_manager_id;
      await api.post('/employees', body);
      toast.success(`${form.name} added — offer letter sent to ${form.personal_email}`);
      setOpen(false); setForm(initialForm); setTaxPreview(null);
      setRmMode('dropdown'); setRmEmail('');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name}? This cannot be undone.`)) return;
    try { await api.delete(`/employees/${id}`); toast.success('Employee removed'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (emp) => {
    setEditing({
      id: emp.id, name: emp.name || '', title: emp.title || '', grade: emp.grade || '',
      department: emp.department || '', phone: emp.phone || '',
      reporting_manager_id: emp.reporting_manager_id || '',
      annual_ctc: emp.annual_ctc != null ? String(emp.annual_ctc) : '',
    });
  };
  const saveEdit = async () => {
    try {
      const body = { ...editing };
      delete body.id;
      if (body.annual_ctc === '' || body.annual_ctc == null) delete body.annual_ctc;
      else body.annual_ctc = +body.annual_ctc;
      Object.keys(body).forEach(k => { if (body[k] === '') delete body[k]; });
      await api.patch(`/employees/${editing.id}`, body);
      toast.success('Updated'); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadOffer = async (emp) => {
    try {
      const { data } = await api.get(`/employees/${emp.id}/offer-letter`);
      const url = URL.createObjectURL(new Blob([data.html], { type: 'text/html' }));
      const w = window.open(url, '_blank');
      if (!w) { URL.revokeObjectURL(url); toast.error('Pop-up blocked — allow pop-ups to view offer letter'); return; }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createManual = async () => {
    if (!manualForm.name || !manualForm.work_email || !manualForm.password) {
      return toast.error('Name, work email and password are required');
    }
    setSaving(true);
    try {
      const body = { ...manualForm };
      if (body.pay_amount) body.pay_amount = +body.pay_amount; else delete body.pay_amount;
      if (body.annual_ctc) body.annual_ctc = +body.annual_ctc;
      else delete body.annual_ctc;
      if (body.employment_type === 'volunteer') { delete body.pay_amount; delete body.annual_ctc; }
      // Resolve email-based RM
      if (rmModeManual === 'email' && rmEmailManual.trim()) {
        const rmId = await resolveRmEmail(rmEmailManual.trim());
        if (!rmId) { setSaving(false); return; }
        body.reporting_manager_id = rmId;
      }
      if (manualForm.role === 'employee' && !body.reporting_manager_id) {
        setSaving(false);
        return toast.error('Reporting manager is required for employees');
      }
      ['title', 'grade', 'department', 'phone', 'reporting_manager_id', 'personal_email'].forEach(k => {
        if (!body[k]) delete body[k];
      });
      await api.post('/employees/manual', body);
      toast.success(`${manualForm.name} added — welcome email sent to ${manualForm.work_email}`);
      setManualOpen(false);
      setManualForm(initialManualForm());
      setRmModeManual('dropdown'); setRmEmailManual('');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // A user can belong to MULTIPLE status buckets. A "resigning" employee is still ACTIVE.
  const resigningIds = new Set(offboardings.map(o => o.employee_id));
  const matchesStatus = (u, status) => {
    if (status === 'all') return true;
    if (status === 'terminated')  return !!u.terminated;
    if (status === 'rejected')    return u.onboarding_status === 'rejected';
    if (status === 'resigning')   return resigningIds.has(u.id) && !u.terminated;
    if (status === 'active')      return !u.terminated && u.onboarding_status === 'verified';
    if (status === 'onboarding')  return !u.terminated && ['offer_sent', 'documents_pending', 'bgc_review', 'bgc_cleared'].includes(u.onboarding_status);
    return true;
  };

  // Build the department options list — use real departments if loaded, else derive from list.
  const deptOptions = (departments.length
    ? departments.map(d => d.name)
    : Array.from(new Set(list.map(u => u.department).filter(Boolean))).sort()
  );

  const filtered = list.filter(u => {
    const q = query.toLowerCase();
    if (q && !(u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || (u.department || '').toLowerCase().includes(q)))
      return false;
    if (statusFilter !== 'all' && !matchesStatus(u, statusFilter)) return false;
    if (jobFilter !== 'all' && (u.employment_type || 'full_time') !== jobFilter) return false;
    if (deptFilter !== 'all' && (u.department || '__none__') !== deptFilter) return false;
    return true;
  });
  // STATUS_OPTIONS / JOB_OPTIONS + filter chip state moved to EmployeesFilterBar.

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Directory</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Employees</h1>
          <p className="text-slate-500 mt-1">
            {list.length} total · {canManage ? 'Search, hire and manage your team.' : 'Browse your direct reports.'}
          </p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2" data-testid="employee-actions">
            <BulkImportDialog onDone={load} />
            <Dialog open={manualOpen} onOpenChange={setManualOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-existing-button" variant="outline"
                        className="rounded-full px-5 h-11 border-slate-300">
                  <Zap className="w-4 h-4 mr-2" strokeWidth={1.8} /> Add staff directly (no offer)
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-existing-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Add staff directly — no offer flow</DialogTitle>
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
                    ⚠️ This dialog is for backfilling employees who are <b>already hired</b> (e.g. CSV migration, payroll-only records).
                    The candidate gets login credentials immediately — <b>no offer letter, no BGC, no acceptance step</b>.
                    To send a proper offer with the full Offer&nbsp;→&nbsp;Accept&nbsp;→&nbsp;BGC&nbsp;→&nbsp;Work&nbsp;credentials flow,
                    close this and use <b>"Hire new team member"</b> instead.
                  </div>
                </DialogHeader>
                <div className="space-y-5 mt-2">
                  <section>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Account credentials</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2"><Label className="text-xs font-semibold text-slate-600">Full name *</Label>
                        <Input data-testid="manual-name" value={manualForm.name} onChange={(e) => setManualForm({...manualForm, name: e.target.value})} className="rounded-xl mt-1" /></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Work email *</Label>
                        <Input data-testid="manual-work-email" type="email" value={manualForm.work_email} onChange={(e) => setManualForm({...manualForm, work_email: e.target.value})} placeholder={user?.tenant?.company_domain ? `name@${user.tenant.company_domain}` : 'name@yourcompany.com'} className="rounded-xl mt-1" /></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Initial password *</Label>
                        <Input data-testid="manual-password" value={manualForm.password} onChange={(e) => setManualForm({...manualForm, password: e.target.value})} placeholder="Set a unique password — employee will change at first login" className="rounded-xl mt-1" /></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Personal email (optional)</Label>
                        <Input data-testid="manual-personal-email" type="email" value={manualForm.personal_email} onChange={(e) => setManualForm({...manualForm, personal_email: e.target.value})} className="rounded-xl mt-1" /></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Phone</Label>
                        <Input data-testid="manual-phone" value={manualForm.phone} onChange={(e) => setManualForm({...manualForm, phone: e.target.value})} className="rounded-xl mt-1" /></div>
                    </div>
                  </section>
                  <section>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Role & position</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs font-semibold text-slate-600">Role *</Label>
                        <Select value={manualForm.role} onValueChange={(v) => setManualForm({...manualForm, role: v})}>
                          <SelectTrigger data-testid="manual-role" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="reporting_manager">Reporting Manager</SelectItem>
                            <SelectItem value="hr">HR</SelectItem>
                            {isAdmin && <SelectItem value="admin">Admin</SelectItem>}
                          </SelectContent>
                        </Select></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Title</Label>
                        <Input data-testid="manual-title" value={manualForm.title} onChange={(e) => setManualForm({...manualForm, title: e.target.value})} className="rounded-xl mt-1" /></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Grade</Label>
                        <Select value={manualForm.grade} onValueChange={(v) => setManualForm({...manualForm, grade: v})}>
                          <SelectTrigger data-testid="manual-grade" className="rounded-xl mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{grades.map(g => <SelectItem key={g.v} value={g.v}>{g.label}</SelectItem>)}</SelectContent>
                        </Select></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Department</Label>
                        <Select value={manualForm.department || undefined} onValueChange={(v) => setManualForm({...manualForm, department: v, reporting_manager_id: ''})}>
                          <SelectTrigger data-testid="manual-department" className="rounded-xl mt-1"><SelectValue placeholder={departments.length ? "Select department" : "No departments — create one first"} /></SelectTrigger>
                          <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                        </Select></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Branch / Work location</Label>
                        <Select value={manualForm.branch_id || undefined} onValueChange={(v) => setManualForm({...manualForm, branch_id: v})}>
                          <SelectTrigger data-testid="manual-branch" className="rounded-xl mt-1"><SelectValue placeholder={branches.length ? "Pick a branch" : "No branches — create one first"} /></SelectTrigger>
                          <SelectContent>
                            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.type === 'headquarters' ? ' (HQ)' : ''}{b.city ? ` · ${b.city}` : ''}</SelectItem>)}
                          </SelectContent>
                        </Select></div>
                      {manualForm.role === 'employee' && (
                        <div className="col-span-2">
                          <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-slate-600">Reporting Manager *</Label>
                            <button type="button" data-testid="manual-rm-mode-toggle" onClick={() => { setRmModeManual(rmModeManual === 'dropdown' ? 'email' : 'dropdown'); setManualForm({...manualForm, reporting_manager_id: ''}); setRmEmailManual(''); }} className="text-xs text-[#3D4A8E] hover:underline">
                              {rmModeManual === 'dropdown' ? 'Enter email manually instead' : 'Pick from list instead'}
                            </button>
                          </div>
                          {rmModeManual === 'dropdown' ? (
                            <Select value={manualForm.reporting_manager_id || undefined} onValueChange={(v) => setManualForm({...manualForm, reporting_manager_id: v})}>
                              <SelectTrigger data-testid="manual-rm" className="rounded-xl mt-1">
                                <SelectValue placeholder={manualForm.department ? ((deptManagersManual[manualForm.department] || []).length ? "Select a reporting manager" : `No one in ${manualForm.department} yet`) : "Pick a department first"} />
                              </SelectTrigger>
                              <SelectContent>
                                {(deptManagersManual[manualForm.department] || []).map(m => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.name} · {m.is_manager ? m.role.replace('_',' ') : 'employee'}{m.title ? ` · ${m.title}` : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input data-testid="manual-rm-email" type="email" value={rmEmailManual} onChange={(e) => setRmEmailManual(e.target.value)} placeholder="manager@lanvaus.com" className="rounded-xl mt-1" />
                          )}
                        </div>
                      )}
                      <div><Label className="text-xs font-semibold text-slate-600">Joined on</Label>
                        <Input data-testid="manual-joined" type="date" value={manualForm.joined_at} onChange={(e) => setManualForm({...manualForm, joined_at: e.target.value})} className="rounded-xl mt-1" /></div>
                      <div><Label className="text-xs font-semibold text-slate-600">Employment type</Label>
                        <Select value={manualForm.employment_type} onValueChange={(v) => setManualForm({...manualForm, employment_type: v, ...(v === 'volunteer' ? { pay_amount: '', annual_ctc: '' } : {})})}>
                          <SelectTrigger data-testid="manual-emp-type" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{EMP_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {manualForm.employment_type !== 'volunteer' && (
                        <>
                          <div><Label className="text-xs font-semibold text-slate-600">Pay structure</Label>
                            <Select value={manualForm.salary_period} onValueChange={(v) => setManualForm({...manualForm, salary_period: v})}>
                              <SelectTrigger data-testid="manual-period" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                              <SelectContent>{PERIODS.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          <div><Label className="text-xs font-semibold text-slate-600">{PERIODS.find(p => p.v === manualForm.salary_period)?.helper} (₹)</Label>
                            <Input data-testid="manual-pay" type="number" value={manualForm.pay_amount} onChange={(e) => setManualForm({...manualForm, pay_amount: e.target.value})} className="rounded-xl mt-1" /></div>
                        </>
                      )}
                      {manualForm.employment_type === 'volunteer' && (
                        <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900" data-testid="manual-volunteer-banner">
                          Volunteer — no compensation, no payslips will be issued.
                        </div>
                      )}
                    </div>
                  </section>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setManualOpen(false)} className="rounded-full">Cancel</Button>
                  <Button data-testid="submit-manual"
                          onClick={createManual}
                          disabled={saving || !manualForm.name || !manualForm.work_email || !manualForm.password}
                          className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                    {saving ? 'Saving…' : 'Create account & send credentials'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={(o) => {
              setOpen(o);
              if (o && !templatesLoaded) {
                api.get('/offer-templates').then(({ data }) => {
                  setOfferTemplates(data);
                  setTemplatesLoaded(true);
                  // Pre-fill the form with the company defaults (HR can still tweak per candidate)
                  setForm((f) => ({
                    ...f,
                    roles_responsibilities: f.roles_responsibilities || data.roles_responsibilities || '',
                    terms_conditions: f.terms_conditions || data.terms_conditions || '',
                  }));
                }).catch(() => { /* silent — defaults will be applied server-side */ });
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-employee-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
                  <UserPlus className="w-4 h-4 mr-2" strokeWidth={1.8} /> Hire new (offer flow)
                </Button>
              </DialogTrigger>
            <DialogContent data-testid="add-employee-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Hire new team member · Offer flow</DialogTitle>
                <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-900">
                  ✅ This sends a <b>public offer letter</b> to the candidate's personal email (no credentials yet).
                  After they <b>accept</b> the offer, they receive their personal-email login to upload BGC documents.
                  Once HR <b>approves the BGC</b>, the system issues their <b>work email</b> and final credentials.
                </div>
              </DialogHeader>
              <div className="space-y-5 mt-2">
                <section>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Candidate</div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { k: 'name', label: 'Full name *' },
                      { k: 'personal_email', label: 'Personal email *', type: 'email' },
                      { k: 'phone', label: 'Phone' },
                    ].map(f => (
                      <div key={f.k} className={f.k === 'phone' ? '' : 'col-span-2 md:col-span-1'}>
                        <Label className="text-xs font-semibold text-slate-600">{f.label}</Label>
                        <Input data-testid={`new-employee-${f.k}`} type={f.type || 'text'}
                               value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                               className="rounded-xl mt-1" />
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Role & position</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Role *</Label>
                      <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                        <SelectTrigger data-testid="new-employee-role" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="employee">Employee</SelectItem>
                          <SelectItem value="reporting_manager">Reporting Manager</SelectItem>
                          <SelectItem value="hr">HR</SelectItem>
                          {isAdmin && <SelectItem value="admin">Admin</SelectItem>}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Title</Label>
                      <Input data-testid="new-employee-title" value={form.title}
                             onChange={(e) => setForm({ ...form, title: e.target.value })}
                             placeholder="e.g. Senior Software Engineer" className="rounded-xl mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Grade</Label>
                      <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                        <SelectTrigger data-testid="new-employee-grade" className="rounded-xl mt-1"><SelectValue placeholder="Select grade" /></SelectTrigger>
                        <SelectContent>
                          {grades.map(g => <SelectItem key={g.v} value={g.v}>{g.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Department</Label>
                      <Select value={form.department || undefined} onValueChange={(v) => setForm({ ...form, department: v, reporting_manager_id: '' })}>
                        <SelectTrigger data-testid="new-employee-department" className="rounded-xl mt-1">
                          <SelectValue placeholder={departments.length ? "Select department" : "No departments — create one first"} />
                        </SelectTrigger>
                        <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Branch / Work location</Label>
                      <Select value={form.branch_id || undefined} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                        <SelectTrigger data-testid="new-employee-branch" className="rounded-xl mt-1">
                          <SelectValue placeholder={branches.length ? "Pick a branch" : "No branches — create one first"} />
                        </SelectTrigger>
                        <SelectContent>
                          {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}{b.type === 'headquarters' ? ' (HQ)' : ''}{b.city ? ` · ${b.city}` : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {form.role === 'employee' && (
                      <div className="col-span-2">
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs font-semibold text-slate-600">Reporting Manager *</Label>
                          <button type="button" data-testid="new-rm-mode-toggle" onClick={() => { setRmMode(rmMode === 'dropdown' ? 'email' : 'dropdown'); setForm({ ...form, reporting_manager_id: '' }); setRmEmail(''); }} className="text-xs text-[#3D4A8E] hover:underline">
                            {rmMode === 'dropdown' ? 'Enter email manually instead' : 'Pick from list instead'}
                          </button>
                        </div>
                        {rmMode === 'dropdown' ? (
                          <Select value={form.reporting_manager_id || undefined} onValueChange={(v) => setForm({ ...form, reporting_manager_id: v })}>
                            <SelectTrigger data-testid="new-employee-rm" className="rounded-xl mt-1">
                              <SelectValue placeholder={form.department ? ((deptManagers[form.department] || []).length ? "Select a reporting manager from this department" : `No one in ${form.department} yet`) : "Pick a department first"} />
                            </SelectTrigger>
                            <SelectContent>
                              {(deptManagers[form.department] || []).map(m => (
                                <SelectItem key={m.id} value={m.id}>
                                  {m.name} · {m.is_manager ? m.role.replace('_',' ') : 'employee'}{m.title ? ` · ${m.title}` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input data-testid="new-rm-email" type="email" value={rmEmail} onChange={(e) => setRmEmail(e.target.value)} placeholder="manager@lanvaus.com" className="rounded-xl mt-1" />
                        )}
                      </div>
                    )}
                  </div>
                </section>

                <section>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Employment & Compensation</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Employment type *</Label>
                      <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v, ...(v === 'volunteer' ? { pay_amount: '', annual_ctc: '' } : {}) })}>
                        <SelectTrigger data-testid="new-employee-emp-type" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{EMP_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {form.employment_type !== 'volunteer' && (
                      <div>
                        <Label className="text-xs font-semibold text-slate-600">Pay structure *</Label>
                        <Select value={form.salary_period} onValueChange={(v) => setForm({ ...form, salary_period: v })}>
                          <SelectTrigger data-testid="new-employee-period" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{PERIODS.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {form.employment_type === 'volunteer' ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900" data-testid="volunteer-banner">
                      Volunteer — <b>no compensation</b>. Offer letter will be generated without a pay clause; no payslips will be issued.
                    </div>
                  ) : (
                    <>
                      <Label className="text-xs font-semibold text-slate-600">{PERIODS.find(p => p.v === form.salary_period)?.helper || 'Amount'} (₹)</Label>
                      <Input data-testid="new-employee-pay" type="number" value={form.pay_amount}
                             onChange={(e) => setForm({ ...form, pay_amount: e.target.value, annual_ctc: '' })}
                             placeholder={form.salary_period === 'hourly' ? 'e.g. 500' : form.salary_period === 'monthly' ? 'e.g. 75000' : 'e.g. 1800000'}
                             className="rounded-xl mt-1" />
                    </>
                  )}
                  {taxPreview && form.employment_type !== 'volunteer' && (
                    <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm" data-testid="tax-preview">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Auto-computed (India new regime FY 2025-26)</div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                        <div className="flex justify-between"><span className="text-slate-500">Monthly gross</span><span className="font-semibold">{inr(taxPreview.monthly_gross)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Monthly net</span><span className="font-semibold text-emerald-700">{inr(taxPreview.monthly_net)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Basic (50%)</span><span>{inr(taxPreview.monthly_basic)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">HRA (30%)</span><span>{inr(taxPreview.monthly_hra)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Allowances</span><span>{inr(taxPreview.monthly_allowances)}</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Monthly TDS</span><span className="text-red-600">{inr(taxPreview.monthly_tax)}</span></div>
                        <div className="flex justify-between col-span-2 pt-2 border-t border-slate-200 mt-1"><span className="text-slate-500">Annual tax</span><span className="font-semibold">{inr(taxPreview.annual_tax)}</span></div>
                      </div>
                    </div>
                  )}

                  {/* Offer-letter terms (shown in offer email + public offer page + PDF) */}
                  <div className="mt-4 bg-indigo-50/50 border border-indigo-200 rounded-xl p-4">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#3D4A8E] mb-3">Offer-letter terms (shown to the candidate)</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-slate-600">Casual leaves / yr</Label>
                        <Input data-testid="form-casual-leaves" type="number" min={0} value={form.casual_leaves}
                               onChange={(e) => setForm({ ...form, casual_leaves: e.target.value })}
                               className="rounded-xl mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-600">Sick leaves / yr</Label>
                        <Input data-testid="form-sick-leaves" type="number" min={0} value={form.sick_leaves}
                               onChange={(e) => setForm({ ...form, sick_leaves: e.target.value })}
                               className="rounded-xl mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-600">Weekly hours</Label>
                        <Input data-testid="form-weekly-hours" type="number" min={0} value={form.weekly_hours}
                               onChange={(e) => setForm({ ...form, weekly_hours: e.target.value })}
                               className="rounded-xl mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-600">Probation (months)</Label>
                        <Input data-testid="form-probation" type="number" min={0} value={form.probation_months}
                               onChange={(e) => setForm({ ...form, probation_months: e.target.value })}
                               className="rounded-xl mt-1" />
                      </div>
                    </div>
                  </div>
                </section>

                <section data-testid="offer-rr-tnc-section">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#3D4A8E]" />
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Roles &amp; responsibilities · Terms &amp; conditions</div>
                    </div>
                    {offerTemplates && (
                      <button type="button" data-testid="reset-offer-templates"
                              className="text-[11px] font-semibold text-[#3D4A8E] hover:underline"
                              onClick={() => setForm((f) => ({
                                ...f,
                                roles_responsibilities: offerTemplates.roles_responsibilities,
                                terms_conditions: offerTemplates.terms_conditions,
                              }))}>
                        Reset to company defaults
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-3">
                    The candidate must acknowledge both sections before they can accept the offer. They&apos;ll also appear in the final offer-letter PDF. Edit the company-wide defaults in <b>Company Profile → Offer templates</b>.
                  </p>
                  <Label className="text-xs font-semibold text-slate-600">Roles &amp; Responsibilities</Label>
                  <MarkdownEditor
                    testid="rr-editor"
                    rows={8}
                    value={form.roles_responsibilities || ''}
                    onChange={(v) => setForm({ ...form, roles_responsibilities: v })}
                    className="mt-1.5"
                  />
                  <Label className="text-xs font-semibold text-slate-600 mt-4 block">Terms &amp; Conditions</Label>
                  <MarkdownEditor
                    testid="tnc-editor"
                    rows={10}
                    value={form.terms_conditions || ''}
                    onChange={(v) => setForm({ ...form, terms_conditions: v })}
                    className="mt-1.5"
                  />
                </section>
              </div>
              <DialogFooter className="mt-2">
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button data-testid="submit-new-employee" onClick={onCreate}
                        disabled={saving || !form.personal_email || !form.name}
                        className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {saving ? 'Saving…' : 'Send offer & create account'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Search + combinable filters (AND) */}
      <EmployeesFilterBar
        query={query} setQuery={setQuery}
        statusFilter={statusFilter} setStatusFilter={setStatusFilter}
        jobFilter={jobFilter} setJobFilter={setJobFilter}
        deptFilter={deptFilter} setDeptFilter={setDeptFilter}
        deptOptions={deptOptions}
        filtered={filtered.length} total={list.length}
      />

      <EmployeesGrid
        items={filtered}
        canManage={canManage} isAdmin={isAdmin} currentUserId={user.id}
        resigningIds={resigningIds}
        onOpen={(id) => nav(`/employees/${id}`)}
        onEdit={openEdit}
        onOffer={downloadOffer}
        onDelete={onDelete}
      />

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="edit-employee-dialog" className="max-w-xl max-h-[90vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-heading text-2xl">Edit employee</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2 grid grid-cols-2 gap-3">
                {[
                  { k: 'name', label: 'Full name' },
                  { k: 'phone', label: 'Phone' },
                  { k: 'title', label: 'Title' },
                ].map(f => (
                  <div key={f.k}>
                    <Label className="text-xs font-semibold text-slate-600">{f.label}</Label>
                    <Input data-testid={`edit-${f.k}`} value={editing[f.k]} onChange={(e) => setEditing({...editing, [f.k]: e.target.value})} className="rounded-xl mt-1" />
                  </div>
                ))}
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Department</Label>
                  <Select value={editing.department || undefined} onValueChange={(v) => setEditing({...editing, department: v})}>
                    <SelectTrigger data-testid="edit-department" className="rounded-xl mt-1"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Grade</Label>
                  <Select value={editing.grade} onValueChange={(v) => setEditing({...editing, grade: v})}>
                    <SelectTrigger data-testid="edit-grade" className="rounded-xl mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {grades.map(g => <SelectItem key={g.v} value={g.v}>{g.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Reporting Manager</Label>
                  <Select value={editing.reporting_manager_id || undefined} onValueChange={(v) => setEditing({...editing, reporting_manager_id: v})}>
                    <SelectTrigger data-testid="edit-rm" className="rounded-xl mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {rms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}{r.department ? ` · ${r.department}` : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs font-semibold text-slate-600">Annual CTC (₹)</Label>
                  <Input type="number" data-testid="edit-ctc" value={editing.annual_ctc} onChange={(e) => setEditing({...editing, annual_ctc: e.target.value})} className="rounded-xl mt-1" />
                  <p className="text-xs text-slate-400 mt-1">Salary structure & TDS will be recomputed automatically.</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
                <Button onClick={saveEdit} data-testid="save-edit" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">Save changes</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
